import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TyreSetDialog } from "@/components/TyreSetDialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { TYRES, formatDate } from "@/lib/format";

const th = "text-left px-3 py-2.5 whitespace-nowrap";
const td = "px-3 py-2";

const TyresTable = ({ set, highlight }) => {
  const { t } = useT();
  return (
    <div data-testid="tyreset-tyres-table" className="bg-[#12141C] border border-[#282D3D] rounded-sm overflow-x-auto">
      <table className="w-full font-mono text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#090A0F]">
          <tr>{["position", "barcode", "circumference", "weight", "hardness"].map((k) => <th key={k} className={th}>{t(k)}</th>)}</tr>
        </thead>
        <tbody>
          {TYRES.map((p) => {
            const ty = set.tyres?.[p] || {};
            const hit = highlight && ty.barcode === highlight;
            return (
              <tr key={p} data-testid={`tyreset-detail-${p}`} data-highlight={hit ? "true" : "false"}
                className={`border-t border-[#282D3D]/60 border-l-2 ${hit ? "bg-[#FFB800]/10 border-l-[#FFB800]" : "border-l-transparent"}`}>
                <td className={`${td} font-heading uppercase font-bold text-base`}>{t(`tyre_${p}`)}</td>
                <td className={`${td} ${hit ? "text-[#FFB800] font-bold" : "text-white"}`}>{ty.barcode || "—"}</td>
                <td className={td}>{ty.circumference ?? "—"}</td>
                <td className={td}>{ty.weight ?? "—"}</td>
                <td data-testid={`tyreset-detail-${p}-hardness`} className={td}>{ty.hardness ?? "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const UsageTable = ({ usage }) => {
  const { t, lang } = useT();
  if (!usage.length) return <div data-testid="tyreset-no-usage" className="border border-dashed border-[#282D3D] rounded-sm p-8 text-center text-slate-500 font-mono text-sm">{t("no_usage")}</div>;
  return (
    <div data-testid="tyreset-usage-table" className="bg-[#12141C] border border-[#282D3D] rounded-sm overflow-x-auto">
      <table className="w-full font-mono text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#090A0F]">
          <tr>{["event", "sessions", "driver", "stint", "laps"].map((k) => <th key={k} className={th}>{t(k)}</th>)}</tr>
        </thead>
        <tbody>
          {usage.map((u) => (
            <tr key={`${u.session_id}-${u.stint}`} data-testid={`tyreset-usage-${u.session_id}-${u.stint}`} className="border-t border-[#282D3D]/60 hover:bg-[#181B26]">
              <td className={`${td} text-slate-400 whitespace-nowrap`}>{formatDate(u.event_date, lang)} · {u.event_name}</td>
              <td className={td}><Link to={`/events/${u.event_id}/sessions/${u.session_id}`} className="text-white hover:text-[#FF3B30]">{u.session_name}</Link></td>
              <td className={`${td} text-[#FF3B30]`}>{u.driver || "—"}</td>
              <td className={td}>S{u.stint}</td>
              <td className={`${td} font-bold text-white`}>{u.laps}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function TyreSetDetail() {
  const { setId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [set, setSet] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/tyre-sets/${setId}`).then((r) => setSet(r.data)).catch(() => navigate("/tyres"));
  }, [setId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    await api.delete(`/tyre-sets/${setId}`);
    toast.success(t("deleted"));
    navigate("/tyres");
  };

  if (!set) return <div className="font-mono text-xs text-slate-500">{t("loading")}</div>;

  return (
    <div className="fade-up">
      <Link to="/tyres" data-testid="back-to-tyres-link" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase text-slate-500 hover:text-white mb-5 transition-colors duration-200">
        <ArrowLeft className="w-3.5 h-3.5" /> {t("tyres_archive_title")}
      </Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-8">
        <div>
          <Link to={`/events/${set.event_id}`} data-testid="tyreset-event-link" className="font-mono text-xs uppercase tracking-wider text-[#FFB800] hover:underline">
            {formatDate(set.event_date, lang)} · {set.event_name}
          </Link>
          <h1 data-testid="tyreset-detail-name" className="font-heading font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-2">{set.name}</h1>
          {set.notes && <p className="text-slate-400 mt-3 text-sm whitespace-pre-line">{set.notes}</p>}
        </div>
        <div className="flex items-end gap-6">
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("total_laps")}</div>
            <div data-testid="tyreset-total-laps" className="font-mono font-bold text-5xl text-white leading-none">{set.total_laps}</div>
          </div>
          <Button variant="outline" data-testid="edit-tyreset-btn" onClick={() => setOpen(true)} className="rounded-sm bg-transparent border-[#282D3D] hover:bg-[#181B26] hover:text-white">
            <Pencil className="w-4 h-4 mr-1.5" /> {t("edit")}
          </Button>
          <ConfirmDelete testid="delete-tyreset" description={t("confirm_tyre_set")} onConfirm={remove} trigger={
            <Button variant="outline" data-testid="delete-tyreset-btn" className="rounded-sm bg-transparent border-[#282D3D] text-slate-400 hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] hover:border-[#FF3B30]"><Trash2 className="w-4 h-4" /></Button>
          } />
        </div>
      </div>
      <TyresTable set={set} highlight={params.get("barcode")} />
      <h2 className="font-heading font-bold uppercase text-2xl sm:text-3xl tracking-tight mt-10 mb-4">{t("usage_history")}</h2>
      <UsageTable usage={set.usage} />
      <TyreSetDialog open={open} onOpenChange={setOpen} tyreSet={set} onSaved={load} />
    </div>
  );
}
